// @LTG-ID: contact-validators
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md#/contact, source `Contact.html` lines 620-678 (v4 v1.1)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A1)
// @OWNER: web
//
// CHG-0083-M-A1 — 6 validators ported verbatim from v4 source `Contact.html`:
//   validateName, validateBusinessName, validateEmail, validateState, validateMessage, validatePhone
//
// Each validator returns { ok: boolean, msg?: string }. Error copy is the
// canonical LTG voice (cheeky but never insulting) from the v4 source — see
// `README - Contact Page.md` "Validation rules and error copy" section.
//
// Implementation notes:
//   - NAME_PATTERN is Unicode-aware (\p{L} letters, \p{M} combining marks) and
//     supports international names: Smith, O'Brien, Mary-Jane, van der Berg,
//     Dr., Jr., St., etc.
//   - validateEmail rejects disposable-domain substrings at intake (mailinator,
//     10minutemail, guerrillamail, tempmail, throwaway, sharklasers).
//   - validatePhone is OPTIONAL: empty value returns { ok: true } per v4 line 673.
//
// No API wiring this turn (CHG-0083-M-A1 boundary); CHG-0084 will wire to
// `/api/contact` with the same validation surface used on the backend.

const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}\p{Zs}'.\-\u00B7]{0,49}$/u;
const HAS_LETTER = /\p{L}/u;
const ONLY_DIGITS = /^\d+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DISPOSABLE_DOMAINS = [
    "mailinator.com",
    "10minutemail",
    "guerrillamail",
    "tempmail",
    "throwaway",
    "sharklasers",
];

export function validateName(value, fieldLabel) {
    const v = (value || "").trim();
    if (!v) {
        return {
            ok: false,
            msg: "We need to put something in the " + fieldLabel + " field.",
        };
    }
    if (v.length < 2) {
        return {
            ok: false,
            msg: "One letter? Generous of you, but we need a bit more.",
        };
    }
    if (ONLY_DIGITS.test(v)) {
        return {
            ok: false,
            msg: "Come on, you are not a cyborg. Letters, please.",
        };
    }
    if (!HAS_LETTER.test(v)) {
        return {
            ok: false,
            msg: "We need at least one letter in there.",
        };
    }
    if (v.length > 50) {
        return {
            ok: false,
            msg: "Even Royals do not have names this long. Trim it down a bit.",
        };
    }
    if (!NAME_PATTERN.test(v)) {
        return {
            ok: false,
            msg: "Letters, spaces, hyphens, and apostrophes only. That covers Smith, O'Brien, and Mary-Jane alike.",
        };
    }
    return { ok: true };
}

export function validateBusinessName(value) {
    const v = (value || "").trim();
    if (!v) {
        return {
            ok: false,
            msg: "Even sole proprietors have a name on the door.",
        };
    }
    if (v.length < 2) {
        return {
            ok: false,
            msg: "A bit short for a business name. Try the full version.",
        };
    }
    if (v.length > 120) {
        return {
            ok: false,
            msg: "We will assume that is the legal name and the d/b/a combined. Trim it.",
        };
    }
    if (ONLY_DIGITS.test(v)) {
        return {
            ok: false,
            msg: "Numbers alone? Bold. We will need at least one letter.",
        };
    }
    if (!HAS_LETTER.test(v) && !/\d/.test(v)) {
        return {
            ok: false,
            msg: "We need letters or numbers, not only symbols.",
        };
    }
    return { ok: true };
}

export function validateEmail(value) {
    const v = (value || "").trim();
    if (!v) {
        return {
            ok: false,
            msg: "We need somewhere to reach you back.",
        };
    }
    if (v.length > 200) {
        return {
            ok: false,
            msg: "That email is suspiciously long. Try a shorter address.",
        };
    }
    if (!EMAIL_PATTERN.test(v)) {
        return {
            ok: false,
            msg: "That does not quite look like an email we can actually reach.",
        };
    }
    const lower = v.toLowerCase();
    if (DISPOSABLE_DOMAINS.some((d) => lower.includes(d))) {
        return {
            ok: false,
            msg: "Disposable email addresses do not reach our inbox. A real one, please.",
        };
    }
    return { ok: true };
}

export function validateState(value) {
    if (!value) {
        return {
            ok: false,
            msg: "Pick a state. It routes you to the right tax desk.",
        };
    }
    return { ok: true };
}

export function validateMessage(value) {
    const v = (value || "").trim();
    if (!v) {
        return {
            ok: false,
            msg: "Tell us something. A sentence or two is fine.",
        };
    }
    if (v.length < 10) {
        return {
            ok: false,
            msg: "A little more context goes a long way. Give us at least a sentence.",
        };
    }
    if (v.length > 5000) {
        return {
            ok: false,
            msg: "That is a lot of context. Try keeping it under 5,000 characters.",
        };
    }
    return { ok: true };
}

export function validatePhone(value) {
    // Optional per v4 source line 673.
    if (!value || !value.trim()) return { ok: true };
    const digits = value.replace(/\D/g, "");
    if (digits.length < 7) {
        return {
            ok: false,
            msg: "That phone number looks too short. Skip the field if you would rather not share it.",
        };
    }
    if (digits.length > 15) {
        return {
            ok: false,
            msg: "That is more digits than any phone system uses.",
        };
    }
    return { ok: true };
}

// US states list — ported verbatim from v4 source `Contact.html` lines 573-585.
// 50 states + DC; DC last per v4 ordering.
export const US_STATES = [
    ["AL", "Alabama"],
    ["AK", "Alaska"],
    ["AZ", "Arizona"],
    ["AR", "Arkansas"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["ME", "Maine"],
    ["MD", "Maryland"],
    ["MA", "Massachusetts"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MS", "Mississippi"],
    ["MO", "Missouri"],
    ["MT", "Montana"],
    ["NE", "Nebraska"],
    ["NV", "Nevada"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NY", "New York"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VT", "Vermont"],
    ["VA", "Virginia"],
    ["WA", "Washington"],
    ["WV", "West Virginia"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
    ["DC", "Washington, D.C."],
];

// Categories — ported verbatim from v4 source `Contact.html` lines 587-606.
// Six entries: snapshot, membership, support, press, partnership, other.
// Icons are inline SVG JSX, rendered by step components.
export const CATEGORY_KEYS = [
    "snapshot",
    "membership",
    "support",
    "press",
    "partnership",
    "other",
];
